/* eslint-disable complexity */

import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { RadioCard } from '../../RadioCard';
import { CandidateState } from '@scale/shared/types/chat-completion';
import { ModelResponseSelectorTab } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseSelector/ModelResponseSelectorTabsWrapper';
import {
  CandidateMessageWithMetadata,
  ChatTaskContextType,
  UIGenerationMetadata,
  UIGenerationEnforcementMode,
} from '@scale/shared/types/chat';
import { Accordion, Icon, Skeleton, Typography, Spinner } from '@scale/scaleui';
import UtilitiesWrapper from 'react-scale/components/scalers/tasks/common/UtilitiesWrapper';
import { AttachmentViewer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AttachmentViewer';
import { useTrans } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useTrans';
import { useMemo, useContext, memo, ReactNode, useCallback, useState } from 'react';
import { getTwoTextDiffContent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { useModelCritiqueForCandidate } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useModelCritique';
import { ModelCritiqueAlert } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseSelector/ModelCritiqueAlert';
import { Alert } from 'react-scale/components/scalers/tasks/ThreadedInteraction/TempComponents';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ChatIFrame } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatIFrame';
import { isNil } from 'lodash';
import { ChatLazarusUIRenderer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatLazarusUIRenderer';
import { ExpandableContent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ui/ExpandableContent';
import { ChatHtmlRenderer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatHtmlRenderer';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { extractTextFromRegex } from '../../utils';
import produce from 'immer';
import { ModelErrorButton } from './ModelErrorButton';

const getDisplayText = (
  candidate: CandidateMessageWithMetadata,
  enableFeedback: boolean,
): string => {
  const texts = [];
  if (candidate.message.content) {
    if (
      candidate.message.messageMetadata?.raw_tool_response_messages &&
      candidate.message.messageMetadata?.raw_tool_response_messages.length > 0
    ) {
      // This is a BX0K Agent Completion specific code path for displaying more readable tool call responses
      if (candidate.message.messageMetadata?.raw_tool_response_messages.length > 1) {
        texts.push(`## Tool Call Responses:`);
      } else {
        texts.push(`## Tool Call Response:`);
      }
      for (const toolCallMsg of candidate.message.messageMetadata?.raw_tool_response_messages) {
        texts.push(`Tool Call id: ${toolCallMsg.tool_call_id}:`);
        try {
          texts.push(
            `\`\`\`json\n${JSON.stringify(JSON.parse(toolCallMsg.content), null, 2)}\n\`\`\``,
          );
        } catch {
          // If the content is not valid JSON, just display it as is
          texts.push(toolCallMsg.content);
        }
      }
    } else {
      texts.push(candidate.message.content);
    }
  }
  if (enableFeedback && candidate.message.messageMetadata?.feedback) {
    const feedback = candidate.message.messageMetadata?.feedback;
    if (Array.isArray(feedback)) {
      let formattedFeedback = '';
      feedback.forEach((item, index) => {
        formattedFeedback += `### Feedback ${index + 1}:\n`;
        formattedFeedback += `Message: ${item.feedback_message}\n`;
        formattedFeedback += `Suggested Action: ${item.suggested_action}\n\n`;
      });
      texts.push(formattedFeedback);
    }
  }
  if (candidate.message.messageMetadata?.tool_calls) {
    const tool_calls = candidate.message.messageMetadata?.tool_calls;
    texts.push(`## Tool Calls:\n \`\`\`json\n${JSON.stringify(tool_calls, null, 2)}\n\`\`\``);
  }
  return texts.join('\n');
};

interface SingleCandidateCardProps {
  candidate: CandidateMessageWithMetadata;
  selectedCandidateId: string;
  onCandidateSelect: (candidateId: string) => void;
  onSetReactGeneratedUrlWithMetadata: (
    candidateId: string,
    urlWithMetadata: {
      reactUIGeneratedUrl?: string;
    } & UIGenerationMetadata,
  ) => void;
  enableRTL: boolean;
  useMarkdownDisplay: boolean;
  chatMessageDisplayConfig: any;
  tab: ModelResponseSelectorTab;
  obfuscate: (content: string) => string;
  disabled?: boolean;
  retryCandidate: (candidateIndex: number) => void;
  retryCooldown: number;
  numRetries?: number;
  exitTask?: () => void;
  enableModelCritique: boolean;
  chatPrompt: string;
  displayIndex: number;
  candidatesAreLoaded: boolean;
  twoCandidateDiffResult: ReturnType<typeof getTwoTextDiffContent> | null;
  stacked?: boolean;
  individualRerollEnabled: boolean | undefined;
  handleIndividualReroll: (candidateIndex: number) => void;
  enableReactUIRendering: boolean | undefined;
  extractModelResponseRegex?: string;
  reactGeneratedUrlWithMetadata?: {
    reactUIGeneratedUrl?: string;
  } & UIGenerationMetadata;
  enableUiGeneration?: boolean;
  uiGenerationEnforcementMode?: UIGenerationEnforcementMode;
  setCodeGenerationEnforcementPassed: (passed: boolean) => void;
}

interface CandidateCardsProps
  extends Omit<
    SingleCandidateCardProps,
    'candidate' | 'displayIndex' | 'candidatesAreLoaded' | 'twoCandidateDiffResult'
  > {
  candidates: CandidateMessageWithMetadata[];
  isReviewConsensus: boolean;
  candidatesAreLoaded: boolean;
  chatModelsLoading: boolean;
  chatModelsError: string | null;
  individualRerollEnabled: boolean | undefined;
  handleIndividualReroll: (candidateIndex: number) => void;
  // This is used to force the loading bar to show up for all candidates in cases where skip_selection is selected
  // You must use this in conjunction with obfuscate to ensure that the content is not displayed
  forceLoadingStateForAllCandidates: boolean;
  reactGeneratedUrlWithMetadataByModel: Record<
    string,
    {
      reactUIGeneratedUrl?: string;
    } & UIGenerationMetadata
  >;
}

export const CandidateCards: React.FC<CandidateCardsProps> = memo(
  ({
    candidates,
    selectedCandidateId,
    onCandidateSelect,
    reactGeneratedUrlWithMetadataByModel,
    onSetReactGeneratedUrlWithMetadata,
    enableRTL,
    useMarkdownDisplay,
    chatMessageDisplayConfig,
    tab,
    obfuscate,
    disabled,
    candidatesAreLoaded,
    retryCandidate,
    retryCooldown,
    numRetries,
    exitTask,
    chatModelsLoading,
    chatModelsError,
    enableModelCritique,
    chatPrompt,
    stacked,
    individualRerollEnabled,
    handleIndividualReroll,
    enableReactUIRendering,
    extractModelResponseRegex,
    forceLoadingStateForAllCandidates,
    enableUiGeneration,
    uiGenerationEnforcementMode,
    setCodeGenerationEnforcementPassed,
  }) => {
    const twoCandidateDiffResult = useMemo(() => {
      if (!candidatesAreLoaded || candidates.length !== 2) {
        return null;
      } else {
        return getTwoTextDiffContent(
          candidates[0]?.message?.content,
          candidates[1]?.message?.content,
        );
      }
    }, [candidatesAreLoaded, candidates]);

    const [
      codeGenerationEnforcementPassedByCandidate,
      setCodeGenerationEnforcementPassedByCandidate,
    ] = useState<Record<string, boolean>>(
      candidates.reduce((acc, candidate) => {
        acc[candidate.id] = true;
        return acc;
      }, {} as Record<string, boolean>),
    );
    const updateCodeGenerationPassedForCandidate = useCallback(
      (candidateId: string, passed: boolean) => {
        setCodeGenerationEnforcementPassedByCandidate(
          produce(codeGenerationEnforcementPassedByCandidate, draft => {
            draft[candidateId] = passed;
          }),
        );
        setCodeGenerationEnforcementPassed(
          Object.values(codeGenerationEnforcementPassedByCandidate).every(Boolean),
        );
      },
      [setCodeGenerationEnforcementPassed, codeGenerationEnforcementPassedByCandidate],
    );

    /**
     * When `skip_selection` is enabled, all candidates should be in the `Loading` state
     * until all of them have finished loading. The only exception is if a candidate errors,
     * in which case it should remain in the `Error` state where it can be retried by the CB.
     */
    const getFinalCandidateState = useCallback(
      (candidate: CandidateMessageWithMetadata) => {
        if (candidate.state === CandidateState.Error) {
          return CandidateState.Error;
        }

        if (forceLoadingStateForAllCandidates) {
          return CandidateState.Loading;
        }

        return candidate.state;
      },
      [forceLoadingStateForAllCandidates],
    );

    if (chatModelsLoading) {
      return (
        <div className="w-full h-full flex items-center justify-center gap-2">
          <Spinner className="" size="xs" variant="primary" />
          <Typography.Subtitle3>Loading chat models...</Typography.Subtitle3>
        </div>
      );
    }

    if (chatModelsError) {
      return (
        <Alert variant="danger">
          <Typography.Paragraph2>{chatModelsError}</Typography.Paragraph2>
        </Alert>
      );
    }

    return (
      <RadioCard.Group
        className={
          stacked
            ? `grid grid-rows-${candidates.length > 1 ? `${candidates.length}` : '1'} gap-4`
            : `grid grid-cols-${candidates.length > 1 ? '2' : '1'} gap-4`
        }
        value={selectedCandidateId}
        onChange={candidateId => {
          onCandidateSelect(candidateId);
        }}
      >
        {!chatModelsLoading &&
          !chatModelsError &&
          candidates.map((candidate, displayIndex) => (
            <SingleCandidateCard
              key={`${candidate.id}-${candidate.index}-${candidate.state}`}
              candidate={{
                ...candidate,
                state: getFinalCandidateState(candidate),
              }}
              displayIndex={displayIndex}
              enableRTL={enableRTL}
              useMarkdownDisplay={useMarkdownDisplay}
              chatMessageDisplayConfig={chatMessageDisplayConfig}
              tab={tab}
              obfuscate={obfuscate}
              disabled={disabled || getFinalCandidateState(candidate) === CandidateState.Error}
              candidatesAreLoaded={candidatesAreLoaded}
              retryCandidate={retryCandidate}
              retryCooldown={retryCooldown}
              numRetries={numRetries}
              exitTask={exitTask}
              enableModelCritique={enableModelCritique}
              chatPrompt={chatPrompt}
              twoCandidateDiffResult={twoCandidateDiffResult}
              selectedCandidateId={selectedCandidateId}
              onCandidateSelect={onCandidateSelect}
              individualRerollEnabled={individualRerollEnabled}
              handleIndividualReroll={handleIndividualReroll}
              enableReactUIRendering={enableReactUIRendering}
              extractModelResponseRegex={extractModelResponseRegex}
              reactGeneratedUrlWithMetadata={reactGeneratedUrlWithMetadataByModel[candidate.id]}
              onSetReactGeneratedUrlWithMetadata={onSetReactGeneratedUrlWithMetadata}
              enableUiGeneration={enableUiGeneration}
              uiGenerationEnforcementMode={uiGenerationEnforcementMode}
              setCodeGenerationEnforcementPassed={enforcementPassed => {
                updateCodeGenerationPassedForCandidate(candidate.id, enforcementPassed);
              }}
            />
          ))}
      </RadioCard.Group>
    );
  },
);

const SingleCandidateCard: React.FC<SingleCandidateCardProps> = ({
  candidate,
  displayIndex,
  enableRTL,
  useMarkdownDisplay,
  chatMessageDisplayConfig,
  tab,
  obfuscate,
  disabled,
  candidatesAreLoaded,
  retryCandidate,
  retryCooldown,
  numRetries,
  exitTask,
  enableModelCritique,
  chatPrompt,
  twoCandidateDiffResult,
  individualRerollEnabled,
  handleIndividualReroll,
  enableReactUIRendering,
  extractModelResponseRegex,
  reactGeneratedUrlWithMetadata,
  onSetReactGeneratedUrlWithMetadata,
  enableUiGeneration,
  uiGenerationEnforcementMode,
  setCodeGenerationEnforcementPassed,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const config = useChatStoreExtract(state => state.config);

  const enableFeedback =
    useUnleashFeatureFlag('enable-feedback-lazarus', { projectId: taskContext.projectId }, false) ??
    false;

  const displayTextRaw = getDisplayText(candidate, enableFeedback);
  const displayText = extractModelResponseRegex
    ? extractTextFromRegex(displayTextRaw, extractModelResponseRegex)
    : displayTextRaw;
  const canExpandContent =
    useUnleashFeatureFlag(
      'chat-enable-expanded-model-response',
      { projectId: taskContext.projectId },
      false,
    ) ?? false;

  const noPayForIncompleteTasks =
    useUnleashFeatureFlag(
      'enable-show-no-pay-for-incomplete-tasks',
      { projectId: taskContext.projectId },
      false,
    ) ?? false;

  const shouldStream = candidate.endpoint?.params.stream;
  // as of writing this comment, only GeminiToolUseTrajectoryStrategy will return a shortResponse in the messageMetadata
  const preview =
    canExpandContent && candidate.message.messageMetadata?.shortResponse ? (
      <CandidatePreviewContent
        canExpandContent={canExpandContent}
        obfuscatedPreviewText={obfuscate(candidate.message.messageMetadata?.shortResponse)}
        useMarkdownDisplay={useMarkdownDisplay}
        chatMessageDisplayConfig={chatMessageDisplayConfig}
        taskContext={taskContext}
        candidatesAreLoaded={candidatesAreLoaded}
        isStreaming={shouldStream}
      />
    ) : undefined;

  const { t } = useTrans();

  const { autoCritiqueValue, setAutoCritiqueValue, autoCritiqueHook } =
    useModelCritiqueForCandidate(enableModelCritique, candidate, displayIndex, chatPrompt);

  const errorMessage = !isNil(candidate.errorMessage)
    ? `Could not connect to the model. ${candidate.errorMessage}`
    : 'Could not connect to the model.';

  return (
    <RadioCard
      value={candidate.id}
      className="h-full shadow-none max-[800px]:w-96 min-[800px]:flex-1"
      subtitle={
        <div className="flex justify-between items-center gap-1">
          <div className="font-medium text-neutral-800">
            {t('response')} {displayIndex + 1}
          </div>
          <div>
            <div className="flex items-center gap-2">
              {individualRerollEnabled && candidate.state === CandidateState.Completed && (
                <Icon
                  className="text-neutral-500"
                  variant="neutral"
                  size="sm"
                  icon="arrow-rotate-left"
                  tooltip="Fetch new response"
                  tooltipProps={{
                    delayDuration: 0,
                  }}
                  disabled={disabled}
                  onClick={e => {
                    e.stopPropagation();
                    handleIndividualReroll(candidate.index);
                  }}
                />
              )}
              {!taskContext.disableResponseSelectorCopying &&
                candidate.state === CandidateState.Completed && (
                  <Icon
                    className="text-neutral-500"
                    variant="neutral"
                    size="sm"
                    icon="copy"
                    tooltip="Copy this response"
                    tooltipProps={{
                      delayDuration: 0,
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(displayText);
                    }}
                  />
                )}
            </div>
          </div>
        </div>
      }
      disabled={disabled}
      data-testid={`radio-card-${candidate.id}`}
    >
      {enableModelCritique && (
        <>
          {autoCritiqueHook?.critiqueData.loading && (
            <Alert variant="info" className="mb-3">
              <div className="flex items-center text-neutral-400 font-medium">
                <Spinner size="xs" />
                &nbsp;&nbsp;Loading Model Critique...
              </div>
            </Alert>
          )}
          {autoCritiqueHook?.critiqueData.fetched && (
            <ModelCritiqueAlert
              autoCritiqueHook={
                autoCritiqueValue?.critique ? autoCritiqueValue : autoCritiqueHook?.critiqueData
              }
              setAutoCritiqueValue={setAutoCritiqueValue}
            />
          )}
        </>
      )}
      {candidate.state === CandidateState.Loading && (
        <>
          <UtilitiesWrapper enableRTL={enableRTL}>
            <div className="min-w-0">
              <CandidateRenderedContent
                canExpandContent={canExpandContent}
                preview={preview}
                obfuscatedDisplayText={obfuscate(displayText)}
                useMarkdownDisplay={useMarkdownDisplay}
                chatMessageDisplayConfig={chatMessageDisplayConfig}
                taskContext={taskContext}
                candidatesAreLoaded={candidatesAreLoaded}
                isStreaming={shouldStream}
              />
            </div>
          </UtilitiesWrapper>
          <UtilitiesWrapper enableRTL={enableRTL}>
            <Skeleton className={shouldStream ? 'w-1' : 'w-full'} />
          </UtilitiesWrapper>
        </>
      )}
      {candidate.state === CandidateState.Completed && (
        <UtilitiesWrapper enableRTL={enableRTL}>
          {tab === ModelResponseSelectorTab.Diff ? (
            <div className="text-neutral-900 text-sm whitespace-pre-wrap overflow-x-auto">
              {displayIndex === 0
                ? twoCandidateDiffResult?.diffResultOriginalText
                : twoCandidateDiffResult?.diffResultNewText}
            </div>
          ) : (
            <div className="min-w-0">
              {config.renderCode && candidate?.message?.content && <ChatIFrame src={displayText} />}
              <CandidateRenderedContent
                canExpandContent={canExpandContent}
                preview={preview}
                obfuscatedDisplayText={obfuscate(displayText)}
                useMarkdownDisplay={useMarkdownDisplay}
                chatMessageDisplayConfig={chatMessageDisplayConfig}
                taskContext={taskContext}
                candidatesAreLoaded={candidatesAreLoaded}
                isStreaming={shouldStream}
              />
            </div>
          )}
        </UtilitiesWrapper>
      )}
      <br />
      {candidate.state === CandidateState.Completed &&
        candidate.attachments &&
        candidate.attachments.length > 0 &&
        (config.displayExecutedCode &&
        candidate.attachments[0] &&
        'metadata' in candidate.attachments[0] &&
        candidate.attachments[0].metadata?.executed_code ? (
          <Accordion
            header={
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <Typography.Subtitle4>Show Executed Code</Typography.Subtitle4>
                </div>
              </div>
            }
            target={
              <UtilitiesWrapper enableRTL={enableRTL}>
                {tab === ModelResponseSelectorTab.Diff ? (
                  <div className="text-neutral-900 text-sm whitespace-pre-wrap">
                    {displayIndex === 0
                      ? twoCandidateDiffResult?.diffResultOriginalText
                      : twoCandidateDiffResult?.diffResultNewText}
                  </div>
                ) : (
                  <div className="min-w-0">
                    <ChatMarkdown
                      className="text-sm"
                      markdown={'```python' + candidate.attachments[0].metadata?.executed_code}
                      autoRunDependenciesComplete={candidatesAreLoaded}
                      loading={!candidatesAreLoaded}
                      enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                      enableSingleDollarTextMath={
                        taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                      }
                      enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
                      enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
                      enableExecutableCodeBlocks={
                        taskContext.codingConfig?.enableExecutableCodeMarkdown ??
                        taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
                      }
                    />
                  </div>
                )}
              </UtilitiesWrapper>
            }
          />
        ) : (
          <AttachmentViewer attachments={candidate.attachments ?? []} />
        ))}
      {candidate.state === CandidateState.Completed && enableReactUIRendering && (
        <ChatLazarusUIRenderer
          src={obfuscate(displayText)}
          lazarusModel={candidate.endpoint?.params?.model}
        />
      )}
      {candidate.state === CandidateState.Completed &&
        (config.showHtmlGen || enableUiGeneration) && (
          <ChatHtmlRenderer
            source="mrs"
            src={obfuscate(displayText)}
            contentUrlWithMetadata={reactGeneratedUrlWithMetadata}
            setContentGeneratedUrlWithMetadata={urlWithMetadata => {
              onSetReactGeneratedUrlWithMetadata(candidate.id, urlWithMetadata);
            }}
            enforcementMode={uiGenerationEnforcementMode}
            setEnforcementPassed={setCodeGenerationEnforcementPassed}
            uid={candidate.id}
          />
        )}
      {candidate.state === CandidateState.Error && (
        <ModelErrorButton
          errorMessage={errorMessage}
          onRetry={async () => {
            retryCandidate(candidate.index);
          }}
          retryCooldown={retryCooldown}
          numRetries={numRetries}
          exitTask={exitTask}
          noPayForIncompleteTasks={noPayForIncompleteTasks}
        />
      )}
    </RadioCard>
  );
};

const CandidatePreviewContent = ({
  canExpandContent,
  obfuscatedPreviewText,
  useMarkdownDisplay,
  chatMessageDisplayConfig,
  taskContext,
  candidatesAreLoaded,
  isStreaming,
}: {
  canExpandContent: boolean;
  obfuscatedPreviewText: string;
  useMarkdownDisplay: boolean;
  chatMessageDisplayConfig: any;
  taskContext: ChatTaskContextType;
  candidatesAreLoaded: boolean;
  isStreaming?: boolean;
}) => {
  if (!canExpandContent) {
    return null;
  }

  if (useMarkdownDisplay) {
    return (
      <ChatMarkdown
        className="text-sm"
        markdown={obfuscatedPreviewText}
        autoRunDependenciesComplete={candidatesAreLoaded}
        loading={!candidatesAreLoaded}
        enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
        enableSingleDollarTextMath={
          chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
        }
        enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
        enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
        enableExecutableCodeBlocks={
          taskContext.codingConfig?.enableExecutableCodeMarkdown ??
          taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
        }
        shouldDebounceContentUpdates={!isStreaming}
      />
    );
  } else {
    return (
      <div className="text-neutral-900 text-sm whitespace-pre-wrap">{obfuscatedPreviewText}</div>
    );
  }
};

const CandidateRenderedContent = ({
  canExpandContent,
  preview,
  obfuscatedDisplayText,
  useMarkdownDisplay,
  chatMessageDisplayConfig,
  taskContext,
  candidatesAreLoaded,
  isStreaming,
}: {
  canExpandContent: boolean;
  preview?: ReactNode;
  obfuscatedDisplayText: string;
  useMarkdownDisplay: boolean;
  chatMessageDisplayConfig: any;
  taskContext: ChatTaskContextType;
  candidatesAreLoaded: boolean;
  isStreaming?: boolean;
}) => {
  let fullContent;
  if (useMarkdownDisplay) {
    fullContent = (
      <ChatMarkdown
        className="text-sm"
        markdown={obfuscatedDisplayText}
        autoRunDependenciesComplete={candidatesAreLoaded}
        loading={!candidatesAreLoaded}
        enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
        enableSingleDollarTextMath={
          chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
        }
        enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
        enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
        enableExecutableCodeBlocks={
          taskContext.codingConfig?.enableExecutableCodeMarkdown ??
          taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
        }
        shouldDebounceContentUpdates={!isStreaming}
        defaultExpandCodeBlocks={false}
      />
    );
  } else {
    fullContent = (
      <div className="text-neutral-900 text-sm whitespace-pre-wrap">{obfuscatedDisplayText}</div>
    );
  }

  if (canExpandContent && preview) {
    return (
      <ExpandableContent preview={preview} expandButtonText="Show full response">
        {fullContent}
      </ExpandableContent>
    );
  }

  return fullContent;
};
